import { TextSelection, AllSelection } from 'prosemirror-state'
import { toggleMark, setBlockType, wrapIn, lift, chainCommands, baseKeymap } from 'prosemirror-commands'
import { history, undo, redo, closeHistory } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import { inputRules, wrappingInputRule, textblockTypeInputRule, InputRule } from 'prosemirror-inputrules'
import { wrapInList, liftListItem, sinkListItem, splitListItem } from 'prosemirror-schema-list'
import * as tables from 'prosemirror-tables'
import { editorSchema as schema, parseHtml } from './schema'
import { cleanStyle } from './html'
import { CompatibleTableView } from './tableView'

export const tableCommands = {
  addRowBefore: tables.addRowBefore,
  addRowAfter: tables.addRowAfter,
  deleteRow: tables.deleteRow,
  addColumnBefore: tables.addColumnBefore,
  addColumnAfter: tables.addColumnAfter,
  deleteColumn: tables.deleteColumn,
  mergeCells: tables.mergeCells,
  splitCell: tables.splitCell,
  toggleHeaderRow: tables.toggleHeaderRow,
  toggleHeaderColumn: tables.toggleHeaderColumn,
  deleteTable: tables.deleteTable
}

export function createPlugins () {
  return [
    history(),
    inputRules({ rules: [
      wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.bullet_list),
      wrappingInputRule(/^(\d+)\.\s$/, schema.nodes.ordered_list, match => ({ order: Number(match[1]) })),
      wrappingInputRule(/^>\s$/, schema.nodes.blockquote),
      textblockTypeInputRule(/^(#{1,6})\s$/, schema.nodes.heading, match => ({ level: match[1].length })),
      // 공백을 입력하면 URL을 자동 링크로 만든다. 붙여넣기는 paste 처리에서 지원한다.
      new InputRule(/(https?:\/\/[^\s<>]+)\s$/, (state, match, start, end) => {
        return state.tr.insertText(' ', end).addMark(start, end, schema.marks.link.create({ htmlAttrs: { href: match[1] } }))
      })
    ] }),
    keymap({
      'Mod-z': undo,
      'Shift-Mod-z': redo,
      'Mod-y': redo,
      'Mod-b': toggleMark(schema.marks.bold),
      'Mod-i': toggleMark(schema.marks.italic),
      'Mod-u': toggleMark(schema.marks.underline),
      Enter: splitListItem(schema.nodes.list_item),
      Tab: chainCommands(tables.goToNextCell(1), sinkListItem(schema.nodes.list_item)),
      'Shift-Tab': chainCommands(tables.goToNextCell(-1), liftListItem(schema.nodes.list_item))
    }),
    keymap(baseKeymap),
    // 표 선택과 드래그 열 너비 조절은 엔진이 문서 모델과 함께 관리한다.
    tables.columnResizing({ View: CompatibleTableView }), tables.tableEditing()
  ]
}

export function markActive (state, name) {
  const type = schema.marks[name]
  if (!type) return false
  const { from, to, empty, $from } = state.selection
  return empty ? !!type.isInSet(state.storedMarks || $from.marks()) : state.doc.rangeHasMark(from, to, type)
}

export function currentMark (state, name) {
  const type = schema.marks[name]
  return type.isInSet(state.storedMarks || state.selection.$from.marks())
}

function withStyle (attrs, property, value) {
  const element = document.createElement('span')
  element.style.cssText = attrs.style || ''
  if (value) element.style.setProperty(property, value)
  else element.style.removeProperty(property)
  const result = { ...attrs }
  const style = cleanStyle(element.style.cssText)
  if (style) result.style = style
  else delete result.style
  return result
}

export function textStyle (property, value) {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection
    const type = schema.marks.text_style
    const tr = state.tr
    if (empty) {
      const old = currentMark(state, 'text_style')
      tr.addStoredMark(type.create({ htmlAttrs: withStyle(old ? old.attrs.htmlAttrs : {}, property, value) }))
    } else {
      // 각 텍스트 조각의 다른 속성을 보존한다. 색 변경 시 기존 크기가 사라지지 않는다.
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isInline) return
        const old = type.isInSet(node.marks)
        tr.addMark(Math.max(from, pos), Math.min(to, pos + node.nodeSize), type.create({ htmlAttrs: withStyle(old ? old.attrs.htmlAttrs : {}, property, value) }))
      })
    }
    if (dispatch) dispatch(tr)
    return true
  }
}

export function blockStyle (property, value) {
  return (state, dispatch) => {
    const tr = state.tr
    state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
      if (node.isTextblock) {
        const htmlAttrs = withStyle(node.attrs.htmlAttrs || {}, property, value)
        if (property === 'text-align') delete htmlAttrs.align
        tr.setNodeMarkup(pos, null, { ...node.attrs, htmlAttrs })
      }
    })
    if (dispatch) dispatch(tr)
    return true
  }
}

export function runCommand (name, state, dispatch) {
  if (name === 'inlineCode') return toggleMark(schema.marks.code)(state, dispatch)
  if (schema.marks[name]) return toggleMark(schema.marks[name])(state, dispatch)
  if (tableCommands[name]) return tableCommands[name](state, dispatch)
  if (name === 'undo') return undo(state, dispatch)
  if (name === 'redo') return redo(state, dispatch)
  if (['left', 'center', 'right', 'justify'].indexOf(name) !== -1) return blockStyle('text-align', name)(state, dispatch)
  if (name === 'selectAll') { dispatch(state.tr.setSelection(new AllSelection(state.doc))); return true }
  if (name === 'bullet_list' || name === 'ordered_list') {
    const existing = ancestor(state, ['bullet_list', 'ordered_list'])
    if (existing && existing.node.type !== schema.nodes[name]) {
      if (dispatch) dispatch(state.tr.setNodeMarkup(existing.pos, schema.nodes[name], { htmlAttrs: withStyle(existing.node.attrs.htmlAttrs, 'list-style-type', '') }))
      return true
    }
    return (existing ? liftListItem(schema.nodes.list_item) : wrapInList(schema.nodes[name]))(state, dispatch)
  }
  if (name.indexOf('listStyle:') === 0) {
    const style = name.slice(10)
    const type = ['disc', 'circle', 'square'].indexOf(style) !== -1 ? schema.nodes.bullet_list : schema.nodes.ordered_list
    const existing = ancestor(state, ['bullet_list', 'ordered_list'])
    const htmlAttrs = withStyle(existing ? existing.node.attrs.htmlAttrs : {}, 'list-style-type', style)
    if (!existing) return wrapInList(type, { htmlAttrs })(state, dispatch)
    if (dispatch) dispatch(state.tr.setNodeMarkup(existing.pos, type, { htmlAttrs }))
    return true
  }
  if (name === 'indent' || name === 'outdent') {
    const listCommand = name === 'indent' ? sinkListItem(schema.nodes.list_item) : liftListItem(schema.nodes.list_item)
    if (listCommand(state, dispatch)) return true
    const element = document.createElement('p')
    element.style.cssText = state.selection.$from.parent.attrs.htmlAttrs.style || ''
    const previous = parseFloat(element.style.marginLeft) || 0
    const next = Math.min(800, Math.max(0, previous + (name === 'indent' ? 40 : -40)))
    return blockStyle('margin-left', next ? next + 'px' : '')(state, dispatch)
  }
  if (name === 'blockquote') return wrapIn(schema.nodes.blockquote)(state, dispatch) || lift(state, dispatch)
  if (name === 'hr') { dispatch(state.tr.replaceSelectionWith(schema.nodes.horizontal_rule.create()).scrollIntoView()); return true }
  if (name === 'unlink') {
    const range = linkRange(state)
    dispatch(state.tr.removeMark(range.from, range.to, schema.marks.link).removeStoredMark(schema.marks.link))
    return true
  }
  if (name === 'removeformat') {
    let tr = state.tr.removeMark(state.selection.from, state.selection.to).setStoredMarks([])
    state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
      if (!node.isTextblock) return
      const htmlAttrs = { ...node.attrs.htmlAttrs }
      delete htmlAttrs.style
      delete htmlAttrs.align
      tr.setNodeMarkup(pos, null, { ...node.attrs, htmlAttrs })
    })
    dispatch(tr)
    return true
  }
  return false
}

export function changeBlock (value, state, dispatch) {
  if (value === 'blockquote') return runCommand(value, state, dispatch)
  const type = value === 'pre' ? schema.nodes.code_block : value === 'p' ? schema.nodes.paragraph : schema.nodes.heading
  return setBlockType(type, value.charAt(0) === 'h' ? { level: Number(value.slice(1)) } : {})(state, dispatch)
}

export function linkRange (state) {
  const { from, to, empty, $from } = state.selection
  if (!empty) return { from, to }
  const mark = currentMark(state, 'link')
  if (!mark) return { from, to }
  let left = from
  let right = to
  state.doc.nodesBetween($from.start(), $from.end(), (node, pos) => {
    if (node.isInline && mark.isInSet(node.marks)) {
      if (pos <= left && pos + node.nodeSize >= left) left = pos
      if (pos <= right && pos + node.nodeSize >= right) right = pos + node.nodeSize
    }
  })
  return { from: left, to: right }
}

export function insertHtml (state, dispatch, html) {
  const doc = parseHtml(html)
  dispatch(closeHistory(state.tr).replaceSelection(doc.slice(0)).scrollIntoView())
}

export function makeTable (rows, columns) {
  const tableRows = []
  for (let row = 0; row < rows; row++) {
    const cells = []
    for (let column = 0; column < columns; column++) cells.push(schema.nodes.table_cell.createAndFill({ htmlAttrs: { style: 'border: 1px solid #cbd5e1; padding: 8px;' } }))
    tableRows.push(schema.nodes.table_row.create(null, cells))
  }
  return schema.nodes.table.create({ htmlAttrs: { style: 'border-collapse: collapse; width: 100%;' } }, tableRows)
}

// 인라인 서식으로 분리된 단어도 찾는다. 블록/이미지 경계를 가로질러 잘못 교체하지 않는다.
export function findMatches (doc, query, caseSensitive) {
  const found = []
  if (!query) return found
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return
    let text = ''
    node.forEach(child => { text += child.isText ? child.text : '\ufffc' })
    const haystack = caseSensitive ? text : text.toLocaleLowerCase()
    const needle = caseSensitive ? query : query.toLocaleLowerCase()
    let index = haystack.indexOf(needle)
    while (index !== -1) {
      found.push({ from: pos + 1 + index, to: pos + 1 + index + query.length })
      index = haystack.indexOf(needle, index + query.length)
    }
    return false
  })
  return found
}

export function selectMatch (view, match) {
  view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, match.from, match.to)).scrollIntoView())
}

export function ancestor (state, names) {
  const { $from } = state.selection
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth)
    if (names.indexOf(node.type.name) !== -1) return { node, pos: $from.before(depth) }
  }
  if (state.selection.node && names.indexOf(state.selection.node.type.name) !== -1) return { node: state.selection.node, pos: state.selection.from }
  return null
}

export function styleAttributes (htmlAttrs, properties) {
  let result = { ...htmlAttrs }
  Object.keys(properties).forEach(property => { result = withStyle(result, property, properties[property]) })
  return result
}



// WEBPACK FOOTER //
// ./src/components/ETC/editor/commands.js